import {IElementContainer} from "../../utils/ui/ElementContainer";
import {Field} from "../../utils/ui/Field";
import {LeanToSettingConfig} from "../../config/LeanToSettingConfig";
import {Templating} from "../../utils/Templating";
import {Main} from "../../Main";
import {SignalConfig} from "../../config/SignalConfig";
import {WallAddon} from "../../scene/addon/WallAddon";
import {ConfirmWindow} from "../../ui/ConfirmWindow";
import {BooleanInput} from "../../utils/ui/BooleanInput";
import {WarningWindow} from "ui/WarningWindow";

/**
 * The view for LeanToSettingConfig.
 */
export class LeanToSetting implements IElementContainer
{

	public element: HTMLElement;

	private _checkBox: HTMLInputElement;
	private _length: Field;
	private _pitch: Field;
	private _drop: Field;

	private _cutFront: Field;
	private _cutBack: Field;
	private _id: string;

	private _leanToSettingConfig: LeanToSettingConfig;

	constructor(config: {id: string; label: string}, leanToSettingConfig: LeanToSettingConfig)
	{
		this._leanToSettingConfig = leanToSettingConfig;
		this._id = config.id;
		this.element = Templating.renderDOMFromScript("leanTo", config);
		const leantoHeader = this.element.querySelector(".leanToHeader");

		// create controls
		const booleanInput = new BooleanInput({addLabel: true, label: `${config.label}: `, id: config.id});
		this._checkBox = booleanInput.input;
		this._checkBox.addEventListener("change", this.onCheckBoxChange);
		this._leanToSettingConfig.enabled.signal.add(this.onValueChange);

		leantoHeader.appendChild(booleanInput.element);

		Main.getInstance().config.building.insetBay.front.count.signal.add(this.checkCuts);
		Main.getInstance().config.building.insetBay.back.count.signal.add(this.checkCuts);

		this._length = this.createField("Width:", leanToSettingConfig.length);
		this._pitch = this.createField("Pitch:", leanToSettingConfig.pitch, "/12");

		this._drop = this.createDropField("Drop:", leanToSettingConfig.drop);

		this._cutFront = this.createField("Cut 1:", leanToSettingConfig.cut_front);
		this._cutBack = this.createField("Cut 2:", leanToSettingConfig.cut_back);

		// start in disabled

		const enabled = leanToSettingConfig.enabled.value;
		this._checkBox.checked = enabled;
		if (!enabled)
		{
			this.disable();
		}

		// listeners when a project is loaded
		leanToSettingConfig.enabled.signal.add(this.onEnabledChange);
		leanToSettingConfig.length.signal.add(this.onLengthChange);
		leanToSettingConfig.pitch.signal.add(this.onPitchChange);
		leanToSettingConfig.drop.signal.add(this.onDropChange);
		leanToSettingConfig.cut_front.signal.add(this.onCutFrontChange);
		leanToSettingConfig.cut_back.signal.add(this.onCutBackChange);

		leanToSettingConfig.cut_front.signal.add(this.checkCutSum);
		leanToSettingConfig.cut_back.signal.add(this.checkCutSum);
		Main.getInstance().config.building.length.signal.add(this.checkCutSum);
		Main.getInstance().config.building.width.signal.add(this.checkCutSum);
	}

	private checkCutSum = async () =>
	{
		const building = Main.getInstance().config.building;
		const isSide = this.isSide;

		const buildingSize = isSide ? building.length.value : building.width.value;

		const cutSum = this._leanToSettingConfig.cut_back.value + this._leanToSettingConfig.cut_front.value;

		if (cutSum >= buildingSize)
		{
			await WarningWindow.open(`The "cut" sizes are too large for one of the Lean-Tos. We reset them to 0. If you want to remove the Lean-To please uncheck the Lean-To button in the Lean-To section.`);

			const resetValue = 0;
			this._leanToSettingConfig.cut_back.value = resetValue;
			this._leanToSettingConfig.cut_front.value = resetValue;
			this._leanToSettingConfig.cut_back.signal.dispatch(resetValue);
			this._leanToSettingConfig.cut_front.signal.dispatch(resetValue);
		}
	};

	private createDropField(label: string, config: SignalConfig<Number>, units?: string)
	{
		var min = <{[id: string]: number}><any>config.min;

		// drop uses different min depending on this.id
		config.min = min[this._id];

		return this.createField(label, config, units);
	}

	private createField(label: string, config: SignalConfig<Number>, units?: string)
	{
		var field = new Field({
			//id: label.toLowerCase(),
			label: label,
			value: config.value,
			min: config.min,
			max: config.max,
			type: "number",
			units: units || ""
		});

		field.input.changeSignal.add(function (value: number)
		{
			config.setValue(value);
		});

		this.element.appendChild(field.element);

		return field;
	}


	private isThereARollup(addons)
	{
		for (var i = 0; i < addons.length; ++i)
		{
			var rollup = addons[i];
			if (rollup instanceof WallAddon)
			{
				if (rollup.itemId.substring(0, 6) === "rollup")
				{
					return true;
				}
			}
		}
		return false;
	}
	private async checkForAddons(whichWall, message)
	{
		var addons = Main.getInstance().scene.building.addons;
		if (addons[whichWall].length > 0)
		{
			if (this.isThereARollup(addons[whichWall]))
			{
				const isConfirmed = await ConfirmWindow.open(message);
				if (isConfirmed)
				{
					for (var i = addons[whichWall].length - 1; i >= 0; --i)
					{
						Main.getInstance().scene.building.scene.destroyAddon(Main.getInstance().scene.building.addons[whichWall][i]);
					}
					addons[whichWall].length = 0;
					return true;
				}
				else
				{
					return false;
				}
			}
		}
		return true;
	}

	private onCheckBoxChange = async (event: Event) =>
	{
		var enabled = this._checkBox.checked;

		if (enabled)
		{
			if (await this.checkForAddons(this._id, `By adding a Lean-to on this side, you need to re-add the doors and windows to new locations!`))
			{
				this.enable();
				this._leanToSettingConfig.enabled.setValue(enabled);
				this._checkBox.checked = true;
			}
			else
			{
				this._checkBox.checked = false;
			}
		}
		else
		{
			this.disable();
			this._leanToSettingConfig.enabled.setValue(enabled);
			this._checkBox.checked = false;
		}
	};

	private onValueChange = (event: Event): void =>
	{
		var enabled = this._checkBox.checked;

		if (enabled)
		{
			this.enable();
		}
		else
		{
			this.disable();
		}
	};

	private enable()
	{
		this.element.classList.remove("disabled");

		this._length.enable();
		this._pitch.enable();
		this._drop.enable();
		this.checkCuts();
	}

	private disable()
	{
		this.element.classList.add("disabled");

		this._length.disable();
		this._pitch.disable();
		this._drop.disable();
		this.disableCuts();
	}

	private disableCuts()
	{
		this._cutBack.disable();
		this._cutFront.disable();
	}

	private get isSide()
	{
		return this._id === "left" || this._id === "right";
	}

	private checkCuts = () =>
	{
		const isSide = this.isSide;
		if (Main.getInstance().config.building.insetBay.front.count.value === 0 && this._checkBox.checked && isSide)
		{
			this._cutFront.enable();
		}
		else
		{
			this._leanToSettingConfig.cut_front.setValue(0);
			this._cutFront.disable();
		}
		if (Main.getInstance().config.building.insetBay.back.count.value === 0 && this._checkBox.checked && isSide)
		{
			this._cutBack.enable();
		}
		else
		{
			this._leanToSettingConfig.cut_back.setValue(0);
			this._cutBack.disable();
		}

		if (!isSide)
		{
			if (Main.getInstance().config.building.insetBay.front.count.value === 0 && this._id === "front" && this._checkBox.checked)
			{
				this._cutBack.enable();
				this._cutFront.enable();
			}
			else if (Main.getInstance().config.building.insetBay.back.count.value === 0 && this._id === "back" && this._checkBox.checked)
			{
				this._cutBack.enable();
				this._cutFront.enable();
			}
			else
			{
				this._leanToSettingConfig.cut_back.setValue(0);
				this._leanToSettingConfig.cut_front.setValue(0);
				this._cutBack.disable();
				this._cutFront.disable();
			}
		}
	}

	// ======================================================================================================
	// project settings loaded

	private onEnabledChange = (value: boolean) =>
	{
		if (value)
		{
			this.enable();
			this._checkBox.checked = true;
		}
		else
		{
			this.disable();
			this._checkBox.checked = false;
		}
	};

	private onLengthChange = (value: number) =>
	{
		this._length.input.value = value;
	};

	private onPitchChange = (value: number) =>
	{
		this._pitch.input.value = value;
	};

	private onDropChange = (value: number) =>
	{
		this._drop.input.value = value;
	};

	private onCutFrontChange = (value: number) =>
	{
		this._cutFront.input.value = value;
	};

	private onCutBackChange = (value: number) =>
	{
		this._cutBack.input.value = value;
	};

	public get isEnabled()
	{
		return this._checkBox.checked;
	}

	public get leanToSettingConfig()
	{
		return this._leanToSettingConfig;
	}
}